import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import { base_contentHover } from 'ext-ui/style/colors'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { buildCardFeedsColumns } from 'src/app/CardFeeds/components/cardFeedsColumns'
import * as style from 'src/app/CardFeeds/components/CardFeedsTable.style'
import CardFeedStatusFilter from 'src/app/CardFeeds/components/CardFeedStatusFilter'
import Box from 'src/components/Box/Box'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import { useTranslate } from 'src/localization/Trns'
import {
  CardFeedResponse,
  CardFeedStatus,
} from 'src/queries/cardFeeds/cardFeeds.models'
import useQueryCardFeeds from 'src/queries/cardFeeds/domain/useQueryCardFeeds'
import { CardFeedsQueryParams } from 'src/queries/cardFeeds/network/cardFeeds.network'

export interface CardFeedsTableProps {
  organizationId: string | undefined
  onSeeCards: (feedId: string) => void
  // The table owns the card-feeds query; these let the parent react to the
  // first unfiltered load without running a second, duplicate query of its own.
  // `onEmpty` fires once a fresh fetch confirms the org has no feeds (no filters
  // applied); `onLoadError` fires when the query errors.
  onEmpty?: () => void
  onLoadError?: () => void
}

const GRID_HEIGHT = 560
// Non-row chrome the grid height has to cover so the last row isn't clipped:
// the shared DataGrid wrapper renders at `height - 60` (see DataGrid.tsx), and
// inside it the grid reserves a 44px column header and a 52px footer (the
// pagination controls). Sizing to rows alone (the old +104 omitted the footer)
// cut off one row.
const GRID_CHROME_HEIGHT = 60 + 44 + 52

const DEFAULT_SORT_MODEL: GridSortModel = [
  { field: 'dateConnected', sort: 'desc' },
]
const DEFAULT_PAGE_SIZE = 25

// Everything the list query depends on, held in one piece of state so each user
// action (search, filter, sort, paginate) is a single setState — hence a single
// render and a single request — independent of React's batching behavior.
interface CardFeedsQuery {
  search: string
  statuses: CardFeedStatus[]
  sortModel: GridSortModel
  page: number
}

const DEFAULT_QUERY: CardFeedsQuery = {
  search: '',
  statuses: [],
  sortModel: DEFAULT_SORT_MODEL,
  page: 0,
}

const CardFeedsTable: React.FC<CardFeedsTableProps> = ({
  organizationId,
  onSeeCards,
  onEmpty,
  onLoadError,
}) => {
  const translate = useTranslate()
  // Live search box text; debounced into `query.search` below.
  const [searchInput, setSearchInput] = useState('')
  const [query, setQuery] = useState<CardFeedsQuery>(DEFAULT_QUERY)

  // Debounce the search box into the query, snapping back to the first page in
  // the same update. Guard on equality so an unchanged term (e.g. on mount)
  // doesn't churn state or refetch.
  useEffect(() => {
    const handle = setTimeout(() => {
      const next = searchInput.trim()
      setQuery((q) => (q.search === next ? q : { ...q, search: next, page: 0 }))
    }, 300)
    return (): void => clearTimeout(handle)
  }, [searchInput])

  // Each criteria change resets to the first page in the same single setState,
  // so the query never transitions through a stale {newCriteria, oldPage} view.
  const handleStatusesChange = useCallback((next: CardFeedStatus[]): void => {
    setQuery((q) => ({ ...q, statuses: next, page: 0 }))
  }, [])

  const handleSortModelChange = useCallback((next: GridSortModel): void => {
    setQuery((q) => ({ ...q, sortModel: next, page: 0 }))
  }, [])

  const handlePaginationModelChange = useCallback(
    (next: GridPaginationModel): void => {
      setQuery((q) => (q.page === next.page ? q : { ...q, page: next.page }))
    },
    [],
  )

  const paginationModel = useMemo<GridPaginationModel>(
    () => ({ page: query.page, pageSize: DEFAULT_PAGE_SIZE }),
    [query.page],
  )

  const view: CardFeedsQueryParams = useMemo(() => {
    const sort = query.sortModel[0]
    // Only send a direction alongside a real field+direction; an empty sort
    // model or a null `sort` must not default the API to an unintended ASC.
    const hasSort = sort?.field !== undefined && sort?.sort != null
    return {
      sortField: hasSort ? sort.field : undefined,
      sortDirection: hasSort
        ? sort.sort === 'desc'
          ? 'DESC'
          : 'ASC'
        : undefined,
      statuses: query.statuses.length > 0 ? query.statuses : undefined,
      search: query.search || undefined,
      page: query.page,
      pageSize: DEFAULT_PAGE_SIZE,
    }
  }, [query])

  const { data, isLoading, isError, isFetchedAfterMount } = useQueryCardFeeds(
    organizationId,
    true,
    view,
  )

  const feeds: CardFeedResponse[] = data?.cardFeeds ?? []
  // The API returns totalPages but not a reliable total row count, and an
  // over-reported totalPages was lighting up a phantom "next" arrow on a single
  // short page. Rather than MUI's unknown-count mode (which didn't honor
  // hasNextPage here), derive a finite rowCount from what we can trust — the rows
  // actually returned. A short page (fewer than pageSize) is the last page, so
  // the count is exact; a full page might have more, so add one to keep the next
  // arrow enabled until we reach a short page.
  const rowsSoFar = query.page * DEFAULT_PAGE_SIZE + feeds.length
  const rowCount = feeds.length < DEFAULT_PAGE_SIZE ? rowsSoFar : rowsSoFar + 1

  const columns = useMemo(
    () => buildCardFeedsColumns({ translate, onSeeCards }),
    [translate, onSeeCards],
  )

  // Drive the "is filtering" copy from the applied (debounced) search so the
  // empty/no-match overlay agrees with the filters actually applied to the query.
  const isFiltering = query.search.length > 0 || query.statuses.length > 0

  useEffect(() => {
    if (isError) onLoadError?.()
  }, [isError, onLoadError])

  // Surface an empty org to the parent (which owns the redirect) only after a
  // fetch this mount with no filters applied — a filtered no-match isn't empty.
  // Gate on the first page too: an empty non-zero page (e.g. paged past the end)
  // means "no rows here", not "org has no feeds", and must not trigger a redirect.
  useEffect(() => {
    if (
      isFetchedAfterMount &&
      !isError &&
      !isFiltering &&
      query.page === 0 &&
      feeds.length === 0
    ) {
      onEmpty?.()
    }
  }, [
    isFetchedAfterMount,
    isError,
    isFiltering,
    query.page,
    feeds.length,
    onEmpty,
  ])

  const noRowsOverlay = (): React.ReactElement => {
    // Three empty states: a filtered no-match, a genuinely empty org (only
    // possible on the first page), and an empty later page. The last is a
    // transient frame: when the true total is an exact multiple of pageSize the
    // finite-rowCount +1 keeps "next" enabled for one more click, landing on an
    // empty page before the grid clamps back to the first page. Show neutral
    // copy there rather than the empty-org copy, which would wrongly imply the
    // org has no feeds.
    let testId = 'card-feeds-empty'
    let message = translate('No card feeds yet.')
    if (isFiltering) {
      testId = 'card-feeds-no-match'
      message = translate('No card feeds match your search or filters.')
    } else if (query.page > 0) {
      testId = 'card-feeds-no-more'
      message = translate('No more card feeds.')
    }
    return (
      <div css={style.overlay} data-testid={testId}>
        {message}
      </div>
    )
  }

  return (
    <Box
      noPadding
      outlineBorder
      css={style.tableCard}
      data-testid="card-feeds-table"
    >
      <div css={style.toolbar}>
        <div css={style.search}>
          <TextInput
            value={searchInput}
            onChange={(e): void => setSearchInput(e.target.value)}
            placeholder={translate('Search...')}
            aria-label={translate('Search card feeds')}
            testId="card-feeds-search"
          />
        </div>
        <CardFeedStatusFilter
          statuses={query.statuses}
          onChange={handleStatusesChange}
        />
      </div>

      {isError ? (
        <div css={style.overlay}>
          <span role="alert" data-testid="card-feeds-error">
            {translate("We couldn't load your card feeds. Please try again.")}
          </span>
        </div>
      ) : isLoading ? (
        <div
          css={style.overlay}
          role="status"
          aria-live="polite"
          data-testid="card-feeds-loading"
        >
          {translate('Loading...')}
        </div>
      ) : (
        <div css={style.gridWrap}>
          <DataGrid<CardFeedsQueryParams, CardFeedResponse>
            height={
              feeds.length > 0
                ? Math.min(
                    feeds.length * DataGridRowHeight.Default +
                      GRID_CHROME_HEIGHT,
                    GRID_HEIGHT,
                  )
                : GRID_HEIGHT
            }
            rows={feeds}
            columns={columns}
            filtersBody={view}
            rowHeight={DataGridRowHeight.Default}
            sortModel={query.sortModel}
            onSortModelChange={handleSortModelChange}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[DEFAULT_PAGE_SIZE]}
            // Finite, derived rowCount (see above) drives the footer range and
            // next/prev enablement; a short page yields the exact total so the
            // next arrow is correctly disabled on the last page.
            rowCount={rowCount}
            // The whole row opens the feed's details (the same destination as
            // the See Cards action). This is a pointer affordance; keyboard/AT
            // users still navigate via the focusable See Cards button, which
            // stops propagation so it isn't double-counted.
            onRowClick={(params): void => onSeeCards(params.row.id)}
            disableColumnFilter
            disableColumnHiding
            disableRowSelectionOnClick
            slots={{ noRowsOverlay }}
            sx={{ '--hover-color': base_contentHover }}
          />
        </div>
      )}
    </Box>
  )
}

export default CardFeedsTable
